/**
 * FASE 24.9 — PASTORAL TONE GUIDE
 *
 * Centralizes all tone, phrasing, and wording used across Kevia.
 * Single source of truth for language decisions.
 *
 * Design principles:
 *  - Calm and mature
 *  - Clear without being blunt
 *  - Human without being theatrical
 *  - Never corporate, never therapeutic overreach
 */

/* ─── Phrase banks ───────────────────────────────────────────── */

export const PHRASES = Object.freeze({

  confirmation: [
    "Listo.",
    "Registrado.",
    "Anotado.",
    "Hecho.",
    "Guardado.",
  ],

  uncertainty: [
    "No tengo certeza sobre eso, pero puedo ayudarte a pensarlo.",
    "No cuento con esa información en este momento.",
    "Eso no lo puedo confirmar desde aquí.",
    "No tengo datos suficientes para estar seguro.",
    "Eso tendría que verificarlo directamente.",
  ],

  gentle_reminder: [
    "Quizás valga la pena revisarlo pronto.",
    "Podría ser buen momento para retomarlo.",
    "Si tienes un momento, podría ayudar revisarlo.",
    "Tal vez convenga darle una mirada esta semana.",
  ],

  suggestion_soft: [
    "Si quieres, podrías…",
    "Quizás podría ser buen momento para…",
    "Una opción podría ser…",
    "Tal vez valga la pena…",
    "Cuando lo veas conveniente, podría ayudar…",
  ],

  encouragement: [
    "Bien.",
    "Buen avance.",
    "Eso suma.",
    "Hay progreso.",
    "Algo es algo.",
  ],

  continuity: [
    "La última vez hablamos de",
    "Recuerdo que estabas trabajando con",
    "Seguías en proceso con",
    "Quedaba pendiente",
    "Tenías en mente",
  ],

  empathy_light: [
    "Entiendo.",
    "Tiene sentido.",
    "Es comprensible.",
    "Eso pasa.",
  ],

  fallback: [
    "No pude procesar eso bien. ¿Puedes reformularlo?",
    "Algo no funcionó bien. ¿Lo intentamos de otra forma?",
    "Hubo un problema de mi parte. Inténtalo de nuevo cuando quieras.",
  ],

  recovery: [
    "Retomando desde donde estábamos.",
    "Continuamos.",
    "Seguimos.",
  ],

  unavailable: [
    "No tengo conexión en este momento, pero puedo ayudarte desde lo que tengo aquí.",
    "Estoy trabajando sin conexión. Te doy lo que puedo desde la información local.",
    "Sin acceso al servidor, pero puedo orientarte desde el contexto disponible.",
  ],
});

/* ─── Pick utilities ─────────────────────────────────────────── */

/**
 * Returns a deterministic phrase from a category, varied by seed.
 * @param {keyof PHRASES} category
 * @param {number} [seed=0]
 * @returns {string}
 */
export function pickPhrase(category, seed = 0) {
  const bank = PHRASES[category];
  if (!bank?.length) return "";
  return bank[Math.abs(seed) % bank.length];
}

/**
 * Returns a soft suggestion opener.
 * @param {number} [seed=0]
 */
export function softSuggestionOpener(seed = 0) {
  return pickPhrase("suggestion_soft", seed);
}

/**
 * Returns a continuity phrase prefix.
 * @param {number} [seed=0]
 */
export function continuityPrefix(seed = 0) {
  return pickPhrase("continuity", seed);
}

/* ─── Confidence-aware phrasing ─────────────────────────────── */

/**
 * Confidence levels for Kevia's knowledge.
 * Used to shape how claims are phrased.
 */
export const TRUST_LEVEL = Object.freeze({
  CONFIRMED:  "confirmed",   // from real Supabase data
  INFERRED:   "inferred",    // from pattern matching / timeline
  REMEMBERED: "remembered",  // from conversation memory
  ASSUMED:    "assumed",     // from contextual inference
  UNCERTAIN:  "uncertain",   // unknown state
});

const TRUST_QUALIFIERS = Object.freeze({
  confirmed:  null,                        // state plainly, no qualifier
  inferred:   "Según lo que he notado,",
  remembered: "Recuerdo que",
  assumed:    "Creo que",
  uncertain:  "No estoy seguro, pero",
});

/**
 * Returns a context-appropriate qualifier for a pastoral claim.
 * @param {keyof TRUST_LEVEL} level
 * @returns {string|null}
 */
export function trustQualifier(level) {
  return TRUST_QUALIFIERS[level] ?? TRUST_QUALIFIERS.uncertain;
}

/* ─── Tone classification ────────────────────────────────────── */

/**
 * Classify a response mood for the current context.
 * Used to adapt response style without being explicit about it.
 * @param {{ urgency?: string, mood?: string }} ctx
 * @returns {"calm"|"supportive"|"direct"|"warm"}
 */
export function classifyTone(ctx = {}) {
  if (ctx.mood === "grief" || ctx.urgency === "critical") return "supportive";
  if (ctx.urgency === "high") return "direct";
  if (ctx.mood === "celebration") return "warm";
  return "calm";
}

/* ─── Forbidden patterns ─────────────────────────────────────── */

/**
 * Patterns that should never appear in Kevia output.
 * Used in prompt safety checks.
 */
export const FORBIDDEN_PATTERNS = [
  /como IA/i,
  /como asistente de inteligencia artificial/i,
  /según mis datos/i,
  /base de datos/i,
  /ticket/i,
  /gestionar/i,
  /optimizar su/i,
  /solución integral/i,
  /kpi/i,
  /métrica[s]?\s+de\s+éxito/i,
  // Access-denial phrases — Kevia is integrated inside Kéves and must never
  // claim it has no access to the system or user data.
  /no\s+tengo\s+acceso\s+a\s+(los?\s+)?datos/i,
  /no\s+tengo\s+acceso\s+a\s+tu/i,
  /no\s+puedo\s+ver\s+tu/i,
  /no\s+puedo\s+acceder\s+a\s+(los?\s+)?datos/i,
  /no\s+tengo\s+acceso\s+a\s+(tu\s+)?aplicaci[oó]n/i,
  /como\s+asistente\s+de\s+ia,?\s+no\s+puedo\s+acceder/i,
];

/**
 * Returns true if text contains corporate/AI jargon that should be avoided.
 */
export function containsForbiddenPatterns(text) {
  return FORBIDDEN_PATTERNS.some((p) => p.test(text ?? ""));
}
